import { toast } from 'react-toastify'
import {
    AuthSessionSchema,
    GuestLoginResponseSchema,
    IAuthSession,
    IGuestLoginRequest,
    IGuestLoginResponse,
    ILoginRequest,
    ISupportLoginResponse,
    SupportLoginResponseSchema,
} from '~/schemas/auth/authSchemas'
import { ApiError } from '~/schemas/errorSchemas'
import { api, apiService, rotateAnonSession } from '~/services/core/ApiService'
import { endSessionService, startSessionService } from './SessionService'

/*
 * Logins da loja. As URLs `auth/login`, `auth/fast-login` e `/auth/custom-login` são exatamente as da lista de logins do
 * ApiService: um 401 delas é credencial errada, não sessão expirada.
 */

const LOGIN_ERROR_MESSAGE = 'Erro ao realizar login! Tente novamente!'

/** Todos os logins mostram o overlay de carregamento. */
const withLoader = { meta: { showLoader: true } }

// =========================================================================
// SECTION 1: E-MAIL E SENHA
// =========================================================================

/** `POST auth/login`. Credencial errada (400 sem conta, 401 senha errada) e outras falhas avisam por toast. */
export const loginService = (credentials: ILoginRequest): Promise<IAuthSession> =>
    apiService(api)
        .post('auth/login', credentials, AuthSessionSchema, withLoader)
        .onSuccess((session) => startSessionService(session))
        .onError(
            (error: ApiError) => {
                const wrongCredentials = error.statusCode === 400 || error.statusCode === 401
                toast.error(wrongCredentials ? 'Usuário ou senha incorretos!' : LOGIN_ERROR_MESSAGE, {
                    autoClose: 2000,
                })
            },
            { useDefaultErrorHandler: false },
        )
        .execute()

// =========================================================================
// SECTION 2: E-MAIL DO CARRINHO (cliente sem conta)
// =========================================================================

/**
 * `POST /auth/custom-login`: cria o cliente temporário e abre a sessão. Com o e-mail já cadastrado a API responde 200
 * com `status: "error"`, sem sessão: quem chama pede a senha e segue pelo `loginService`. O token deste login não
 * expira na API (`GenerateTokenWithoutExpires`); o cookie usa a mesma validade dos outros logins
 * (`getTokenMaxAgeSeconds`), e é ela que encerra a sessão do cliente sem conta.
 */
export const guestLoginService = (email: string): Promise<IGuestLoginResponse> => {
    const payload: IGuestLoginRequest = { name: '', email, token: '' }
    return apiService(api)
        .post('/auth/custom-login', payload, GuestLoginResponseSchema, withLoader)
        .onSuccess((result) => {
            if (result.status === 'success') startSessionService(result)
        })
        .onError(
            () => {
                toast.error(LOGIN_ERROR_MESSAGE, { autoClose: 2000 })
            },
            { useDefaultErrorHandler: false },
        )
        .execute()
}

// =========================================================================
// SECTION 3: LOGIN SOCIAL (link do glofi-auth)
// =========================================================================

/** `GET auth/fast-login?c=`. Erro sem aviso: a tela mostra o modal de link inválido. */
export const socialLoginService = (code: string): Promise<IAuthSession> =>
    apiService(api)
        .get('auth/fast-login', AuthSessionSchema, { ...withLoader, params: { c: code } })
        .onSuccess((session) => startSessionService(session))
        .onError(() => {}, { useDefaultErrorHandler: false })
        .execute()

// =========================================================================
// SECTION 4: ACESSO DE SUPORTE (atendente entra como o cliente)
// =========================================================================

/**
 * `GET auth/login-as-customer?c=`. Antes, encerra a sessão que houver no navegador (o token de outro cliente não vai
 * na chamada) e abre uma sessão anônima nova, para o carrinho anônimo do atendente não ser juntado ao do cliente. A
 * sessão aberta vale `expiration` segundos. Erro sem aviso: a tela mostra o modal de link inválido.
 */
export async function supportLoginService(code: string): Promise<ISupportLoginResponse> {
    endSessionService()
    // Troca de verdade: apagar só o cookie não basta, o núcleo guarda a sessão anônima também em memória.
    await rotateAnonSession()

    return apiService(api)
        .get('auth/login-as-customer', SupportLoginResponseSchema, { ...withLoader, params: { c: code } })
        .onSuccess((session) => startSessionService(session, { supportExpiration: session.expiration }))
        .onError(() => {}, { useDefaultErrorHandler: false })
        .execute()
}
